import type { Query, QueryClient } from "@tanstack/react-query";

/**
 * Neuladen nach Datenänderungen (Demo und Supabase gleich). Zwei Quellen:
 *
 *  - „lokal“: eine eigene schreibende Aktion (insert, update, remove, rpc, Serverfunktion). Was die Datenbank dabei über
 *    Wächter und Trigger mitändert, ist von hier aus nicht sichtbar; darum lädt sie alle Abfragen außer dem Konto.
 *  - „echtzeit“: ein Ereignis aus Supabase Realtime (0024) zu einer Tabelle. Es lädt nur die Abfragen, die diese Tabelle
 *    zeigen (ECHTZEIT_ABFRAGEN, aus den Schlüsseln im Code abgeleitet; im Zweifel etwas breiter).
 *
 * Entprellung: Änderungen kurz hintereinander (Rechnung plus Positionen plus Freigabe, eine Kette von Glocken-Ereignissen)
 * lösen ein gemeinsames Neuladen aus, WARTEZEIT nach der letzten Änderung, spätestens HOECHSTENS nach der ersten.
 * Abfragen, die nach der Änderung schon frisch gestartet sind (etwa durch useAction direkt nach der Aktion), bleiben
 * unangetastet; so lädt eine Aktion nicht zweimal alles.
 */

export type DatenAenderung = { quelle: "lokal" } | { quelle: "echtzeit"; tabelle: string };

/** Anfang eines Abfrageschlüssels; "*" passt auf jedes Element an dieser Stelle. */
export type SchluesselMuster = readonly string[];

/**
 * Tabellen mit Echtzeit-Abonnement (supabase-api.ts, subscribeRealtime); jede braucht einen Eintrag in ECHTZEIT_ABFRAGEN.
 * Praxistest G-136 (Last): Rechnungen, Verträge und Termine sind seit 0203 nicht mehr in der Veröffentlichung; jede Änderung
 * dort lief sonst für jeden verbundenen Nutzer durch die Zeilenprüfung. Sie laden beim Zurückkehren ins Fenster und über die
 * Glocke neu. Ihre Einträge in ECHTZEIT_ABFRAGEN bleiben für den Fall, dass sie wieder abonniert werden.
 */
export const ECHTZEIT_TABELLEN = ["messages", "message_reactions", "notifications", "notification_states"] as const;

/**
 * Welche Abfragen eine Echtzeit-Tabelle betrifft (Abonnement in supabase-api.ts, subscribeRealtime). Neue Tabellen im
 * Abonnement ohne Eintrag hier laden zur Sicherheit alles außer dem Konto.
 */
export const ECHTZEIT_ABFRAGEN: Readonly<Record<string, readonly SchluesselMuster[]>> = {
  // Chat: Gesprächsliste, Verlauf, Zähler, Kontakte, Absender (Creator), Verlauf in der Kooperation, eigene Chat-Dateien
  messages: [["my_chat_threads"], ["messages"], ["unread"], ["chat_contacts"], ["chat_senders"], ["deals", "*", "kooperation"], ["creator", "*", "chat-dateien"]],
  message_reactions: [["messages"]],
  // Glocke: offene und erledigte Hinweise, Zähler (Menü, Agenturwechsler)
  notifications: [["my_notifications"], ["notifications"], ["unread"]],
  notification_states: [["my_notifications"], ["notifications"], ["unread"]],
  // Kalender und Zähler „neue Termine“ am Menüpunkt
  calendar_events: [["calendar_events"], ["kalender_neu"]],
  // Rechnungen: Listen und Einzelseiten, Auszahlungen, Kennzahlen und Buchungen (Trigger), Kooperationen, Monate,
  // Übersichten, Kalender (Fälligkeiten), Verknüpfungen im Chat und in der Glocke, Creator-Portal
  invoices: [
    ["invoices"], ["invoice_detail"], ["invoice_payout_info"], ["invoice_lines"], ["invoice_payments"], ["invoice_events"], ["invoice_draft"],
    ["payout_candidates"], ["payout_runs"], ["payout_run_items"], ["withholding"], ["bookings"], ["deal_periods"], ["deals"], ["bank_statement_lines"],
    ["ksk"], ["studio_uebersicht"], ["org_setup_status"], ["calendar_events"], ["chat_links"], ["notification_context"],
    ["creator", "*", "base"], ["creator", "*", "archived"], ["creator", "*", "invoice-details"], ["creator", "*", "archived-payout"], ["creator", "*", "marke"],
    ["creator-revenue-years"],
  ],
  // Verträge: Listen und Einzelseiten, Verknüpfungen mit Kooperationen, Kontingent im Tarif, Kalender (Laufzeiten),
  // Verknüpfungen im Chat, Creator-Portal
  contracts: [
    ["contracts"], ["contract_deal_links"], ["deals", "*", "kooperationen"], ["deals", "*", "kooperation"], ["org_entitlements"], ["studio_uebersicht"], ["org_setup_status"], ["calendar_events"], ["chat_links"],
    ["creator", "*", "contracts"], ["creator", "*", "vertraege"], ["creator", "*", "vertrag"], ["creator", "*", "base"],
  ],
};

export function passt(muster: SchluesselMuster, key: readonly unknown[]): boolean {
  if (key.length < muster.length) return false;
  return muster.every((m, i) => m === "*" || key[i] === m);
}

/** Abfragen, die eine Echtzeit-Tabelle betrifft; null: Tabelle ohne Eintrag (dann alles außer dem Konto). */
export function betroffen(tabelle: string): ((key: readonly unknown[]) => boolean) | null {
  const liste = ECHTZEIT_ABFRAGEN[tabelle];
  if (!liste) return null;
  return (key) => liste.some((m) => passt(m, key));
}

const nieNeu = (key: readonly unknown[]) => key[0] === "account";

export const WARTEZEIT = 500;
export const HOECHSTENS = 2000;

/**
 * Verbindet Datenänderungen mit dem Abfrage-Speicher. `abonnieren` ist api.onDataChange; Rückgabe beendet die Verbindung.
 * `jetzt` und die Zeiten sind für Tests austauschbar.
 */
export function neuLadenBeiAenderung(
  qc: QueryClient,
  abonnieren: (cb: (a?: DatenAenderung) => void) => () => void,
  opts: { wartezeit?: number; hoechstens?: number; jetzt?: () => number } = {},
): () => void {
  const wartezeit = opts.wartezeit ?? WARTEZEIT;
  const hoechstens = opts.hoechstens ?? HOECHSTENS;
  const jetzt = opts.jetzt ?? Date.now;
  // Beginn des letzten Ladens je Abfrage: Wer nach einer Änderung gestartet ist, kennt sie schon
  const gestartet = new WeakMap<Query, number>();
  const offSpeicher = qc.getQueryCache().subscribe((e) => {
    if (e.type === "updated" && e.action.type === "fetch") gestartet.set(e.query, jetzt());
  });
  let alles = 0;
  const tabellen = new Map<string, number>();
  let erste = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const ausfuehren = () => {
    timer = null;
    erste = 0;
    const allesSeit = alles;
    const je = [...tabellen].map(([tabelle, seit]) => ({ seit, test: betroffen(tabelle) }));
    alles = 0;
    tabellen.clear();
    const veraltet = (q: Query, seit: number) => (gestartet.get(q) ?? 0) <= seit;
    void qc.invalidateQueries({
      predicate: (q) => {
        if (nieNeu(q.queryKey)) return false;
        if (allesSeit && veraltet(q, allesSeit)) return true;
        return je.some((t) => veraltet(q, t.seit) && (t.test ? t.test(q.queryKey) : true));
      },
    });
  };

  const offAenderung = abonnieren((a) => {
    const t = jetzt();
    if (a?.quelle === "echtzeit") tabellen.set(a.tabelle, t);
    else alles = t;
    if (!erste) erste = t;
    if (timer) clearTimeout(timer);
    timer = setTimeout(ausfuehren, Math.max(0, Math.min(wartezeit, erste + hoechstens - t)));
  });

  return () => {
    offAenderung();
    offSpeicher();
    if (timer) clearTimeout(timer);
    timer = null;
  };
}
